import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Modal, ModalBody, ModalHeader, Row, Button, Spinner, Form, ModalFooter, Label, Input, FormFeedback, Alert } from 'reactstrap';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import OSelect from '../../../Components/OElements/OSelect';
import { getEstablishmentList, getEstablishmentTypeList, getFeedingBargesList, getMaintenanceCompanyList, getMaintenanceTypeList, getPersonList, getPowerGenerationSourceList as getPowerGenerationSourceListApi } from '../../../helpers/establishment/backend_helper';
import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { addNewActivity, addNewPowerGenerationMaintenance, getActivityGroupList, getMaintenanceStatusCode, getPowerGenerationSourceList } from '../../../slices/thunks';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import OHr from '../../../Components/OElements/OHr';
import useActivityMetadata from '../../../Components/Hooks/useActivityMetadataHooks';
dayjs.extend(utc);

const ActivityMaintenanceModal = ({ modal, toggle, activity, categories }) => {
  const dispatch = useDispatch();

  // Estado y funciones de control
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEstablishmentType, setSelectedEstablishmentType] = useState(null);
  const [selectedEstablishment, setSelectedEstablishment] = useState(null);
  const [selectedFeedingBarges, setSelectedFeedingBarges] = useState(null);
  const [selectedPowerGenerationSource, setSelectedPowerGenerationSource] = useState(null);
  const [powerGenerationSourceList, setPowerGenerationSourceList] = useState([]);

  const [type, setType] = useState('-');
  const [type_fuel, setTypeFuel] = useState('-');
  const [power_generation_status, setStatus] = useState('-');
  const [hour_meter_last, setHourMeterLast] = useState(0);
  const [date_hour_meter_last, setDateHourMeterLast] = useState(null);

  const selectorPowerGenerationSource = createSelector(
    state => state.PowerGenerationSource,
    pgs => ({
      powerGenerationSourceListSelect: pgs.powerGenerationSourceListSelect,
      isCollecting: pgs.isPowerGenerationSourceSuccess
    })
  );
  const { powerGenerationSourceListSelect, isCollecting } = useSelector(selectorPowerGenerationSource);

  const selectorMaintenance = createSelector(
    state => state.MaintenanceStatus,
    status => ({
      itemMaintenanceStatusDetail: status.itemMaintenanceStatusDetail
    })
  );
  const { itemMaintenanceStatusDetail } = useSelector(selectorMaintenance);


  const { activityGroup, activityStatus, activityPriority, activityType, activityPeriodicity, normativePeriodicity } = useActivityMetadata({
    groupCode: 'MAN',  // Código del grupo que quieres buscar
    statusCode: 'PND',  // Código del estado
    priorityCode: 'A',  // Código de la prioridad
    typeCode: 'TNO',  // Código del tipo
    activityPeriodicityCode: 'E',  // Código de la periodicidad
    normativePeriodicityCode: 'E'  // Código de la periodicidad
  });

  // Manejo de validación con Formik
  const validation = useFormik({
    initialValues: {
      maintenance_type_id: '',
      feeding_barges_id: '',
      establishment_id: '',
      establishment_type_id: '',
      power_generation_source_id: '',
      maintenance_company_id: null,
      external: false,
      date_ini: new Date(),  // Valor por defecto para la fecha de inicio
      date_end: new Date(),  // Valor por defecto para la fecha de fin
      person_id: '',
      observation: null
    },
    validationSchema: Yup.object({
      maintenance_type_id: Yup.string().required('Campo requerido'),
      establishment_type_id: Yup.string().required('Campo requerido'),
      feeding_barges_id: Yup.lazy(() =>
        selectedEstablishmentType?.barges_needed
          ? Yup.string().required('Campo requerido')
          : Yup.string().nullable()
      ),
      establishment_id: Yup.lazy(() =>
        selectedEstablishmentType && !selectedEstablishmentType.barges_needed
          ? Yup.string().required('Campo requerido')
          : Yup.string().nullable()
      ),
      power_generation_source_id: Yup.string().required('Campo requerido'),
      // date_ini: Yup.date().required('Campo requerido'),
      // date_end: Yup.date().required('Campo requerido'),
      date_ini: Yup.date()
        .required('Requerido')
        .max(Yup.ref('date_end'), 'La fecha de inicio no puede ser mayor a la fecha de término'),
      date_end: Yup.date()
        .required('Requerido')
        .min(Yup.ref('date_ini'), 'La fecha de término no puede ser menor a la fecha de inicio'),
      // person_id: Yup.string().required('Campo requerido'),
      person_id: Yup.lazy((_, { parent }) => {
        return parent.external
          ? Yup.string().nullable()
          : Yup.string().required('Campo requerido');
      }),
    
      maintenance_company_id: Yup.lazy((_, { parent }) => {
        return parent.external
          ? Yup.string().required('Campo requerido')
          : Yup.string().nullable();
      }),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);


      const formatDateTime = (date, hour) => {
        return dayjs(date).hour(hour).minute(0).second(0).format('YYYY-MM-DDTHH:mm:ss');
      };

      const generateSummary = (source) => {
        const parts = [`Mantención programada para el equipo ${source.name?.toUpperCase()}`];

        if (source.brand) {
          parts.push(`Marca: ${source.brand.toUpperCase()}`);
        }
        if (source.model) {
          parts.push(`Modelo: ${source.model.toUpperCase()}`);
        }
        if (source.serial_number) {
          parts.push(`Nro de Serie: ${source.serial_number.toUpperCase()}`);
        }

        return parts.join(', ');
      };

      const generateTitle = (source, establishment, feedingBarge) => {
        let baseTitle = 'Mantención Programada';

        // Si se selecciona un establecimiento, se agrega al título
        if (establishment) {
          baseTitle += ` - Establecimiento: ${establishment.name?.toUpperCase()}`;
        }

        // Si se selecciona un Artefacto Naval, se agrega al título
        if (feedingBarge) {
          baseTitle += ` - Artefacto Naval: ${feedingBarge.name?.toUpperCase()}`;
        }

        // Si se selecciona una fuente de generación, se puede agregar también
        if (source) {
          baseTitle += ` - Equipo: ${source.name?.toUpperCase()}`;
        }

        return baseTitle;
      };

      // Crear la actividad del calendario
      const data_activity = {
        activity_group_id: activityGroup._id,
        activity_status_id: activityStatus._id,
        activity_priority_id: activityPriority._id,
        activity_type_id: activityType._id,
        periodicity_id: normativePeriodicity._id,
        establishment_id: values.establishment_id || null,
        title: generateTitle(selectedPowerGenerationSource, selectedEstablishment, selectedFeedingBarges),  // Generar el título dinámico
        summary: generateSummary(selectedPowerGenerationSource),  // Generar el resumen dinámico con mayúsculas
        start: formatDateTime(values.date_ini, 9),  // Inicio a las 9:00 AM
        end: formatDateTime(values.date_end, 22),   // Fin a las 10:00 PM
        responsible_id: values.person_id || null,
        company_id: values.maintenance_company_id || null,
      };

      let activity = await dispatch(addNewActivity(data_activity));

 

      // Crear la mantención con la referencia a la actividad
      const data = {
        maintenance_status_id: itemMaintenanceStatusDetail._id,
        maintenance_type_id: values.maintenance_type_id,
        establishment_type_id: values.establishment_type_id,
        establishment_id: values.establishment_id || null,
        feeding_barges_id: values.feeding_barges_id || null,
        power_generation_source_id: values.power_generation_source_id,
        date_ini: formatDateTime(values.date_ini, 9),  // Inicio a las 9:00 AM
        date_end: formatDateTime(values.date_end, 22),   // Fin a las 10:00 PM
        person_id: values.person_id,
        observation: values.observation || null,
        external: values.external,
        maintenance_company_id: values.maintenance_company_id || null,
        hour_meter: selectedPowerGenerationSource.hour_meter || 0,
        activity_id: activity.payload._id  // Referencia a la actividad recién creada
      };

      dispatch(addNewPowerGenerationMaintenance(data));

      setIsLoading(false);
      toggle(); // Cierra el modal al finalizar
    },
  });

  // Funciones para manejar la selección de campos

  const handleEstablishmentTypeSelect = (selected) => {
    setSelectedEstablishmentType(selected);
    validation.setFieldValue('establishment_type_id', selected?._id || '');
    setSelectedEstablishment(null);
    setSelectedFeedingBarges(null);
    setSelectedPowerGenerationSource(null);
    validation.setFieldValue('establishment_id', '');
    validation.setFieldValue('feeding_barges_id', '');
    validation.setFieldValue('power_generation_source_id', '');
  };

  const handleEstablishmentSelect = (selected) => {
    setSelectedEstablishment(selected);
    validation.setFieldValue('establishment_id', selected?._id || '');
    setSelectedFeedingBarges(null);
    setSelectedPowerGenerationSource(null);
    setType('-');
    setTypeFuel('-');
    setStatus('-');
    setHourMeterLast('-');
    setDateHourMeterLast('-');
    validation.setFieldValue('feeding_barges_id', '');
    validation.setFieldValue('power_generation_source_id', '');
  };

  const handleFeedingBargesSelect = (selected) => {
    setSelectedFeedingBarges(selected);
    validation.setFieldValue('feeding_barges_id', selected?._id || '');
    setSelectedEstablishment(null);
    setSelectedPowerGenerationSource(null);
    setType('-');
    setTypeFuel('-');
    setStatus('-');
    setHourMeterLast('-');
    setDateHourMeterLast('-');
    validation.setFieldValue('establishment_id', selected?.establishment_id ?? null);
    validation.setFieldValue('power_generation_source_id', '');
  };

  const handlePowerGenerationSourceSelect = (selected) => {
    if (selected) {
      setSelectedPowerGenerationSource(selected);
      setType(selected.power_generation_type?.name.toUpperCase());
      setTypeFuel(selected.fuel_type?.name.toUpperCase());
      setStatus(selected.power_generation_status?.name.toUpperCase());
      setHourMeterLast(selected.hour_meter);
      setDateHourMeterLast(selected.date_hour_meter ? dayjs(selected.date_hour_meter).format('DD-MM-YYYY HH:mm') : '');
    }
  };

  // Determinar si se muestran las opciones de "Establecimiento" o "Artefacto Naval"
  const showEstablishment = selectedEstablishmentType && !selectedEstablishmentType.barges_needed;
  const showFeedingBarges = selectedEstablishmentType?.barges_needed;

  // Función para restablecer el formulario y estados al cerrar el modal
  const resetModalState = () => {
    validation.resetForm();
    setSelectedEstablishmentType(null);
    setSelectedEstablishment(null);
    setSelectedFeedingBarges(null);
    setSelectedPowerGenerationSource(null);
    setType('-');
    setTypeFuel('-');
    setStatus('-');
    setHourMeterLast('-');
    setDateHourMeterLast('-');
  };

  // Reiniciar valores cuando el modal se cierra
  useEffect(() => {
    if (!modal) {
      resetModalState();
    }
  }, [modal]);

  useEffect(() => {
    dispatch(getMaintenanceStatusCode('PND'))
  }, [dispatch]);

  useEffect(() => {
    dispatch(getPowerGenerationSourceList({
      establishment_id: (
        selectedEstablishment
          ? selectedEstablishment._id
          : null
      ),
      feeding_barges_id: (
        selectedFeedingBarges
          ? selectedFeedingBarges._id
          : null
      )
    }));
  }, [selectedEstablishment, selectedFeedingBarges]);

  return (
    <Modal
      isOpen={modal}
      id="activity-modal"
      size="lg"
      className="border-0"
      modalClassName="modal fade zoomIn"
      backdrop="static"
      centered
    >
      <Form onSubmit={(e) => {
        e.preventDefault();
        if (Object.keys(validation.errors).length != 0) { toast.error('Por favor, revisa la información ingresada. Hemos encontrado algunos detalles que necesitan corrección antes de continuar.', { autoClose: 4000 }); }
        validation.handleSubmit();
        return false;
      }}>
        <ModalHeader toggle={toggle}>
          Agendar Actividad de Mantención
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col lg={4}>
              <OSelect
                label="Tipo de Mantención"
                id="maintenance_type_id"
                name="maintenance_type_id"
                isRequired={true}
                value={validation.values.maintenance_type_id || ''}
                requestApi={() => getMaintenanceTypeList({})}
                validation={validation}
              />
            </Col>
            <Col lg={4}>
              <OSelect
                label="Tipo de Establecimiento"
                id="establishment_type_id"
                name="establishment_type_id"
                isRequired={true}
                value={validation.values.establishment_type_id || ''}
                onSelect={handleEstablishmentTypeSelect}
                requestApi={() => getEstablishmentTypeList({})}
                validation={validation}
              />
            </Col>

            {showEstablishment && (
              <Col lg={4}>
                <OSelect
                  key={selectedEstablishmentType?._id || 'establishment_id'}
                  label="Establecimiento"
                  id="establishment_id"
                  name="establishment_id"
                  isRequired={true}
                  value={validation.values.establishment_id || ''}
                  onSelect={handleEstablishmentSelect}
                  requestApi={() => getEstablishmentList({ establishment_type_id: selectedEstablishmentType._id })}
                  validation={validation}
                />
              </Col>
            )}

            {showFeedingBarges && (
              <Col lg={4}>
                <OSelect
                  label="Artefacto Naval"
                  id="feeding_barges_id"
                  name="feeding_barges_id"
                  isRequired={true}
                  value={validation.values.feeding_barges_id || ''}
                  onSelect={handleFeedingBargesSelect}
                  requestApi={() => getFeedingBargesList({})}
                  validation={validation}
                />
              </Col>
            )}

            {(((selectedEstablishment || selectedFeedingBarges)) && powerGenerationSourceListSelect.length > 0) ? (
              <Col lg={4}>
                <OSelect
                  key={selectedEstablishment?._id + selectedFeedingBarges?._id + powerGenerationSourceListSelect.length + 'power_generation_source_id'}
                  label="Fuentes de Generación de Energía"
                  id="power_generation_source_id"
                  name="power_generation_source_id"
                  value={validation.values.power_generation_source_id || ''}
                  onSelect={handlePowerGenerationSourceSelect}
                  requestApi={() => getPowerGenerationSourceListApi({
                    establishment_id: (
                      selectedEstablishment
                        ? selectedEstablishment._id
                        : null
                    ),
                    feeding_barges_id: (
                      selectedFeedingBarges
                        ? selectedFeedingBarges._id
                        : null
                    )
                  })}
                  validation={validation}
                />
              </Col>
            ) : (
              <Col lg={12}>
                <Alert color="primary">Debe contar con una Fuente de Generación de Energía</Alert>
              </Col>
            )}


            {((selectedEstablishment || selectedFeedingBarges) && powerGenerationSourceListSelect.length) ?
              <React.Fragment>
                <Col lg={4}>
                  <Label for='type' className='form-label'>Tipo</Label>
                  <Input name='type' id='type' placeholder='-' type='text' className='form-control' disabled={true} value={type} />
                </Col>

                <Col lg={4}>
                  <Label for='type_fuel' className='form-label'>Tipo Combustible</Label>
                  <Input name='type_fuel' id='type_fuel' placeholder='-' type='text' className='form-control' disabled={true} value={type_fuel} />
                </Col>

                <Col lg={4}>
                  <Label for='power_generation_status' className='form-label'>Estado</Label>
                  <Input name='power_generation_status' id='power_generation_status' placeholder='-' type='text' className='form-control' disabled={true} value={power_generation_status} />
                </Col>

                <Col lg={4}>
                  <Label for='hour_meter_last' className='form-label'>Horómetro</Label>
                  <Input name='hour_meter_last' id='hour_meter_last' placeholder='-' type='text' className='form-control' disabled={true} value={hour_meter_last} />
                </Col>

                <Col lg={4}>
                  <Label for='date_hour_meter_last' className='form-label'>Fecha Último Registro</Label>
                  <Input name='date_hour_meter_last' id='date_hour_meter_last' placeholder='-' type='text' className='form-control' disabled={true} value={date_hour_meter_last} />
                </Col>
              </React.Fragment>
              : null
            }
            <OHr text={'Agenda'} mt={true} />

            <Col sm={3}>
              <DatesProvider settings={{ locale: 'es-mx', firstDayOfWeek: 1, weekendDays: [0], timezone: 'UTC' }}>
                <DatePickerInput
                  label='Fecha Inicio'
                  id='date_ini'
                  name='date_ini'
                  isRequired={true}
                  placeholder="Seleccione una fecha"
                  value={validation.values.date_ini || new Date()} // Valor por defecto
                  onChange={(date) => {
                    validation.setFieldValue("date_ini", date);
                  }}
                  onBlur={validation.handleBlur}
                  valueFormat='DD-MM-YYYY'
                  minDate={new Date(new Date().setDate(new Date().getDate() - 365))} // Set minDate to 5 days before today
                  maxDate={validation.values.date_end ? new Date(validation.values.date_end) : undefined}
                  error={
                    validation.touched['date_ini'] && validation.errors['date_ini']
                      ? validation.errors['date_ini']
                      : ''
                  }
                  clearable={true}
                />
              </DatesProvider>
            </Col>

            <Col sm={3}>
              <DatesProvider settings={{ locale: 'es-mx', firstDayOfWeek: 1, weekendDays: [0], timezone: 'UTC' }}>
                <DatePickerInput
                  label='Fecha Término'
                  id='date_end'
                  name='date_end'
                  placeholder="Seleccione una fecha"
                  value={validation.values.date_end || new Date()} // Valor por defecto
                  onChange={(date) => {
                    validation.setFieldValue("date_end", date);
                  }}
                  onBlur={validation.handleBlur}
                  valueFormat='DD-MM-YYYY'
                  minDate={
                    validation.values.date_ini
                      ? new Date(validation.values.date_ini)
                      : new Date(new Date().setDate(new Date().getDate() - 365))
                  }
                  error={
                    validation.touched['date_end'] && validation.errors['date_end']
                      ? validation.errors['date_end']
                      : ''
                  }
                  clearable={true}
                />
              </DatesProvider>
            </Col>

            <Col lg={2}>
              <div className="form-check form-switch form-switch-md d-flex flex-column p-0" dir="ltr">
                <label className="form-check-label" htmlFor="external">
                  ¿Externo?
                </label>
                <input
                  type="checkbox"
                  className="form-check-input"
                  style={{ marginLeft: '10px' }}
                  id="external"
                  name="external"
                  checked={validation.values.external}
                  onChange={() => {
                    const isExternal = !validation.values.external;

                    validation.setFieldValue('external', isExternal);

                    if (isExternal) {
                      // Si se selecciona "Externo", limpiar person_id
                      validation.setFieldValue('person_id', '');
                    } else {
                      // Si se deselecciona "Externo", limpiar maintenance_company_id
                      validation.setFieldValue('maintenance_company_id', '');
                    }
                  }}
                />
              </div>
            </Col>


            <Col lg={4} hidden={validation.values.external}>
              <OSelect
                label="Responsable"
                id="person_id"
                name="person_id"
                isRequired={!validation.values.external}
                value={validation.values.person_id || ''}
                requestApi={() => getPersonList({})}
                validation={validation}
              />
            </Col>
            <Col lg={4} hidden={!validation.values.external}>
              <OSelect
                label="Empresa de Mantenimiento"
                id="maintenance_company_id"
                name="maintenance_company_id"
                isRequired={validation.values.external}
                value={validation.values.maintenance_company_id || ''}
                requestApi={() => getMaintenanceCompanyList({})}
                validation={validation}
              />
            </Col>

            <Col lg={12}>
              <Label for='observation' className='form-label'>
                Observación
              </Label>
              <Input
                name='observation'
                id='observation'
                className='form-control'
                placeholder='Ingrese una observación'
                type='textarea'
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.observation || ''}
                invalid={validation.touched.observation && validation.errors.observation ? true : false}
              />
              {validation.touched.observation && validation.errors.observation && (
                <FormFeedback type='invalid'>
                  {validation.errors.observation}
                </FormFeedback>
              )}
            </Col>

          </Row>
        </ModalBody>
        <ModalFooter>
          <Button type="button" onClick={toggle} className="btn-danger btn-label">
            <i className="icon-x label-icon"></i>Cancelar
          </Button>
          <button type="submit" className="btn btn-label btn-primary" disabled={isLoading}>
            {isLoading ? <i className='label-icon'><Spinner size='sm' /></i> : <i className="ri-save-3-line label-icon"></i>}
            Agendar Mantención
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

ActivityMaintenanceModal.propTypes = {
  modal: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  activity: PropTypes.object,
};

export default ActivityMaintenanceModal;
